import { useLayoutEffect, type RefObject } from "react";

/** Lowest visible y-coordinate for `el`: the window bottom, or higher when an ancestor clips its
 *  overflow — e.g. `.app-main`, which scrolls the page and ends above the fixed `.app-footer`. */
function visibleBottom(el: HTMLElement): number {
    let bottom = window.innerHeight;
    for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
        if (getComputedStyle(node).overflowY !== "visible") {
            bottom = Math.min(bottom, node.getBoundingClientRect().bottom);
        }
    }
    return bottom;
}

/** Caps a dropdown panel's height to the space actually left below its top edge, so its bottom
 *  never runs off-screen or under the footer on short viewports (users had to zoom out to reach
 *  the last rows). Sets the `--fit-max-height` custom property on the element — the stylesheet
 *  decides how to use it (e.g. `max-height: var(--fit-max-height)` plus `overflow-y: auto`). */
export function useFitToViewport(ref: RefObject<HTMLElement>, active: boolean, bottomMargin = 16): void {
    useLayoutEffect(() => {
        const el = ref.current;
        if (!active || !el) return;
        const fit = () => {
            const top = el.getBoundingClientRect().top;
            el.style.setProperty("--fit-max-height", `${Math.max(160, visibleBottom(el) - top - bottomMargin)}px`);
        };
        fit();
        window.addEventListener("resize", fit);
        window.addEventListener("scroll", fit, true);
        return () => {
            window.removeEventListener("resize", fit);
            window.removeEventListener("scroll", fit, true);
        };
    }, [ref, active, bottomMargin]);
}
